import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function AdminUsage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/usage"); setRows(r.data || []); }
      catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (user && !user.super_admin) {
    return (
      <div className="p-16 max-w-lg mx-auto text-center" data-testid="admin-usage-forbidden">
        <ShieldAlert className="w-12 h-12 mx-auto text-rose-500 mb-4" />
        <div className="font-display font-black text-2xl text-slate-900 mb-2">Super-admin only</div>
        <div className="text-slate-500">This page is reserved for the BidForge platform owner.</div>
        <Link to="/dashboard" className="mt-6 inline-block bf-pill px-5 py-2 text-xs tracking-widest uppercase font-display font-bold" data-testid="admin-usage-back">Back to dashboard</Link>
      </div>
    );
  }

  const totalMonth = rows.reduce((s, r) => s + (r.used_this_month || 0), 0);
  const totalLifetime = rows.reduce((s, r) => s + (r.lifetime_used || 0), 0);
  const totalPurchased = rows.reduce((s, r) => s + (r.lifetime_purchased || 0), 0);

  return (
    <div className="p-8 stagger-in" data-testid="admin-usage-page">
      <div className="bf-eyebrow inline-block text-rose-600 mb-3">SUPER-ADMIN</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900 mb-3">AI credit <span className="bf-grad-text">usage</span>.</h1>
      <p className="text-slate-500 mb-8 max-w-2xl">Per-workspace AI consumption from your Emergent LLM key. Sorted by month-to-date usage.</p>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">MTD USAGE</div>
          <div className="font-display font-black text-3xl bg-gradient-to-br from-violet-500 to-fuchsia-500 bg-clip-text text-transparent" data-testid="usage-mtd">{totalMonth.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">LIFETIME USED</div>
          <div className="font-display font-black text-3xl bg-gradient-to-br from-emerald-500 to-teal-500 bg-clip-text text-transparent">{totalLifetime.toLocaleString()}</div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-5">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">LIFETIME PURCHASED</div>
          <div className="font-display font-black text-3xl bg-gradient-to-br from-amber-500 to-orange-500 bg-clip-text text-transparent">{totalPurchased.toLocaleString()}</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono font-bold tracking-widest text-slate-500 uppercase">
              <tr>
                <th className="text-left px-5 py-3">Workspace</th>
                <th className="text-left px-5 py-3">Plan</th>
                <th className="text-right px-5 py-3">Included</th>
                <th className="text-right px-5 py-3">Used MTD</th>
                <th className="text-right px-5 py-3">Add-on</th>
                <th className="text-right px-5 py-3">7-day events</th>
                <th className="text-right px-5 py-3">Lifetime</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="7" className="text-center py-8 text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-500">No workspaces have used credits yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.workspace_id} className="border-t border-slate-100 hover:bg-violet-50/30" data-testid={`usage-row-${r.workspace_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{r.owner_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.owner_email}</div>
                  </td>
                  <td className="px-5 py-3"><span className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">{(r.plan || "starter").toUpperCase()}</span></td>
                  <td className="px-5 py-3 text-right font-mono text-slate-700">{(r.included_monthly ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">{(r.used_this_month ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right font-mono text-emerald-600">{(r.addon_balance ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-500">{r.events_last_7d ?? 0}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-500">{(r.lifetime_used ?? 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
